import { useLocation } from "react-router";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { PaletteToggle } from "@/components/theme/palette-toggle";
import type { UseSitesResult } from "@/hooks/use-sites";

const TITLES: Record<string, string> = {
  "/": "Home",
  "/shipments": "Shipment Explorer",
  "/orders": "Order Explorer",
  "/admin": "Admin",
};

export interface SiteHeaderProps {
  sites: UseSitesResult;
}

/** Adapted from prism-next's dashboard-01 block — SidebarTrigger + page title;
 *  the site switcher (moved out of the old top header) lives on the right. */
export function SiteHeader({ sites }: SiteHeaderProps) {
  const location = useLocation();
  const title = TITLES[location.pathname] ?? "Shipment Explorer";
  // The env switcher only makes sense on the data surfaces — hidden on Home
  // (which has no env) and Admin, so `/` stays chrome-light.
  const onDataSurface = location.pathname === "/shipments" || location.pathname === "/orders";

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
        <h1 className="text-base font-medium">{title}</h1>
        <div className="ml-auto flex items-center gap-2">
          {onDataSurface && sites.status === "ready" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  {sites.selectedSiteInfo?.name ?? "Select environment"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sites.sites.map((s) => (
                  <DropdownMenuItem key={s.project_id} onSelect={() => sites.selectSite(s.project_id)}>
                    {s.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <PaletteToggle />
          <ThemeToggle variant="outline" size="icon-sm" />
        </div>
      </div>
    </header>
  );
}
